import { GetServerSideProps, NextPage } from "next";
import dynamic from "next/dynamic";
import parser from "ua-parser-js";
import { canonicalLink } from "@helpers/url";
import useResponsive from "@helpers/useResponsive";
import { getBackgroundLead } from "@libraries/api/background-lead/background-lead.repos";
import { getBanner } from "@libraries/api/banner/banner.repositories";
import { getPopularProduct } from "@libraries/api/popular-product/popular-product.repos";
import { getProductMenu } from "@libraries/api/product-menu/product-menu.repos";
import { getTestimony } from "@libraries/api/testimony/testimony.repos";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { localeKey } from "@constants/locale-key";
import { getArticle } from "@libraries/api/articles/articles.repos";

const Desktop = dynamic(() => import("@containers/LandingPage/libs/Desktop"), { ssr: true });
const HeaderLP = dynamic(() => import("@layouts/Main/lib/Header/HeaderLP").then(mod => mod.HeaderLP), { ssr: false });
const Mobile = dynamic(() => import("@containers/LandingPage/libs/MobilePwa").then(mod => mod.default), { ssr: false });

interface LandingPageProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  datas: any;
  isMobile: boolean;
}

const LandingPage: NextPage<LandingPageProps> = ({ datas, isMobile }) => {
  const mobileView = useResponsive("sm", true, isMobile);

  // Show Desktop only after client-side mount AND on desktop screens
  if (!mobileView) {
    return (
      <>
        <HeaderLP />
        <Desktop {...datas} />
      </>
    );
  }

  // Responsive fallback to Mobile (when resized to small screen)
  return (
    <Mobile {...datas} page="home" />
  );
};

export const getServerSideProps: GetServerSideProps = async ({ locale, req }) => {
  const isMobile = req ? parser(req.headers["user-agent"] || "").device.type === "mobile" : false;
  const lang = `${locale}`;
  const EN = lang === "en";

  const responseBanner = await getBanner(lang);
  const responsePopularProduct = await getPopularProduct(7, lang);
  const responseTestimony = await getTestimony(lang);
  const responseBackgroundLead = await getBackgroundLead();
  const responseInsightList = await getArticle(lang);

  const { data: dataProductMenu } = (await getProductMenu({ level: 0 }, lang)) || {};
  const { data: dataBanner } = responseBanner
    ? JSON.parse(JSON.stringify(responseBanner))
    : {};
  const { data: dataPopularProduct } = responsePopularProduct
    ? JSON.parse(JSON.stringify(responsePopularProduct))
    : {};
  const { data: dataTestimony } = responseTestimony
    ? JSON.parse(JSON.stringify(responseTestimony))
    : {};
  const { data: dataBackgroundLead } = responseBackgroundLead
    ? JSON.parse(JSON.stringify(responseBackgroundLead))
    : {};
  const { data: dataArticle } = responseInsightList
    ? JSON.parse(JSON.stringify(responseInsightList))
    : {};

  const datas = {
    banner: dataBanner || null,
    popularProduct: dataPopularProduct || null,
    testimony: dataTestimony || null,
    backgroundLead: dataBackgroundLead?.image || null,
    dataNavbar: dataProductMenu || null,
    articleCustomer: dataArticle || null
  };

  return {
    props: {
      ...(await serverSideTranslations(lang, localeKey)),
      title: EN ? "Your Business Digital Transformation Partner" : "Partner Transformasi Digital Terbaik Bisnis Anda",
      description: EN
        ? "My Telkom Enterprise Solution offers a variety of digital solutions to help your business more efficiently and safely"
        : "My Telkom Enterprise Solution menawarkan berbagai solusi digital untuk membantu bisnis Anda dengan lebih efisien dan aman",
      imageUrl: "https://storeio.cloud.playcourt.id/ewz-mytens-pub-dev/mytensassets/favicon/icon-mytens-white-rectangle.png",
      canonical: canonicalLink("/", locale),
      datas,
      isMobile,
    },
  };
};

export default LandingPage;
